import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Select,
  MenuItem,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { selectFilterRoleMembers } from "store/user/selectors";
import { useAppSelector } from "utils/hooks/storeHooks";
import {
  getFilterRoleByID,
  getRolesAsFilterOptions,
  swicthFilterRoleMemberByIDs,
} from "store/filterRole/thunks";
import variables from "../../styles/partials/_variables.module.scss";
import Modal from "components/Modal/Modal";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import { selectFilterRoleOptions } from "store/filterRole/selectors";
import { RoleData } from "./RolesTable";

type MembersTableProps = {
  currentRoleId: number;
  currentRoleName: string;
  changedMemberId?: (memberId: number) => void; // Id of the member that switched roles (id used for the undo action on the toast notif)
};

type MemberData = {
  uid: number;
  name: string;
  role: string;
  location: string;
};

const MembersTable = ({
  currentRoleId,
  currentRoleName,
  changedMemberId,
}: MembersTableProps) => {
  const dispatch = useDispatch();
  const members = useAppSelector(selectFilterRoleMembers);
  const availableRoles: RoleData[] = useAppSelector(selectFilterRoleOptions);
  const [selectedRoleName, setSelectedRoleName] = useState(currentRoleName);
  const [selectedMemberId, setSelectedMemberId] = useState(currentRoleId);
  const [selectedRoleId, setSelectedRoleId] = useState(0);

  const [openModal, setOpenModal] = useState(false);
  const rows: MemberData[] = useMemo(() => {
    const membersWithRoles: MemberData[] = [];
    if (members) {
      members.forEach((member) => {
        if (member.profile?.filterRoleId === currentRoleId)
          membersWithRoles.push({
            uid: member.id,
            name: member.name,
            role: currentRoleName,
            location: "Los Angeles, CA",
          });
      });
    }
    return membersWithRoles;
  }, [members, currentRoleId, currentRoleName]);

  useEffect(() => {
    dispatch(getRolesAsFilterOptions());
    dispatch(getFilterRoleByID(Number(currentRoleId)));
    setSelectedRoleId(currentRoleId);
  }, [dispatch, currentRoleId]);

  useEffect(() => {
    setSelectedRoleName(currentRoleName);
  }, [currentRoleName]);

  // Handlers for interacting with the change role modal
  const handleOpenModal = (userId: number) => {
    setSelectedMemberId(userId);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedRoleName(currentRoleName);
    setSelectedRoleId(currentRoleId);
    setOpenModal(false);
  };

  const handleChooseRole = (chosenRoleId: number, chosenRoleName: string) => {
    setSelectedRoleId(chosenRoleId);
    setSelectedRoleName(chosenRoleName);
  };

  const handleSubmitChange = (newRoleId: number, userId: number) => {
    dispatch(swicthFilterRoleMemberByIDs(currentRoleId, newRoleId, userId));
    if (changedMemberId) changedMemberId(userId);
    handleCloseModal();
  };

  return (
    <TableContainer component={Paper} style={{ maxHeight: 400 }}>
      <Table aria-label="simple table" stickyHeader sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell width="220px">NAME</TableCell>
            <TableCell align="left" width="220px">
              ROLE
            </TableCell>
            <TableCell align="left" width="220px">
              LOCATION
            </TableCell>
            <TableCell width="48px" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length ? (
            rows.map((row: MemberData) => (
              <TableRow
                key={row.name}
                sx={{
                  "&:nth-of-type(odd)": {
                    backgroundColor: variables.backgroundBlue,
                  },
                  fontSize: "0.875rem",
                }}
              >
                <TableCell component="th" scope="row">
                  {row.name}
                </TableCell>
                <TableCell align="left">{row.role}</TableCell>
                <TableCell align="left">{row.location}</TableCell>
                <TableCell align="right" sx={{ paddingLeft: "0.5rem" }}>
                  <Button
                    endIcon={<ChangeCircleIcon color="primary" />}
                    variant="icon"
                    onClick={() => handleOpenModal(row.uid)}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow sx={{ backgroundColor: variables.backgroundBlue }}>
              <TableCell align="center" colSpan={4}>
                There are no members assigned to this role
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <Modal
          actionLabel="Yes, change"
          cancelActionLabel="Cancel"
          isActionBtnDisabled={selectedRoleName === currentRoleName}
          modalContent={
            <Select
              fullWidth
              value={selectedRoleName}
              defaultValue={currentRoleName}
            >
              {availableRoles.map((option) => (
                <MenuItem
                  key={option.name}
                  value={option.name}
                  onClick={() => handleChooseRole(option.id, option.name)}
                >
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          }
          open={openModal}
          padding="3.5rem"
          title="Change Role"
          titleHelperText={`Changing roles will affect user permissions. Are you sure you want to change this user's role?`}
          width="700px"
          onClose={handleCloseModal}
          onSubmit={() => handleSubmitChange(selectedRoleId, selectedMemberId)}
        />
      </Table>
    </TableContainer>
  );
};

export default MembersTable;
